import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import './AdminPaymentSettings.css';

const METHODS = [
  { key: 'bkash',  label: 'bKash',  color: '#E2136E', icon: '💳' },
  { key: 'nagad',  label: 'Nagad',  color: '#F7941D', icon: '💰' },
  { key: 'rocket', label: 'Rocket', color: '#8B008B', icon: '🚀' },
  { key: 'upay',   label: 'Upay',   color: '#00A859', icon: '📲' },
];

const emptyForm = { number: '', bkash: false, nagad: false, rocket: false, upay: false, isActive: false, note: '' };

const AdminPaymentSettings = () => {
  const [settings, setSettings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);

  const fetchSettings = () => {
    setLoading(true);
    api.get('/admin/payment-settings')
      .then(({ data }) => setSettings(data))
      .catch(() => toast.error('Failed to load payment settings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSettings(); }, []);

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditId(s._id);
    setForm({ number: s.number, bkash: s.bkash, nagad: s.nagad, rocket: s.rocket, upay: s.upay, isActive: s.isActive, note: s.note || '' });
    setShowForm(true);
  };

  const handleToggle = (key) => setForm(f => ({ ...f, [key]: !f[key] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.number.trim()) { toast.error('Enter a phone number'); return; }
    if (!form.bkash && !form.nagad && !form.rocket && !form.upay) {
      toast.error('Select at least one payment method');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const { data } = await api.put(`/admin/payment-settings/${editId}`, form);
        setSettings(prev => prev.map(s => s._id === editId ? data : s));
        toast.success('Payment setting updated');
      } else {
        const { data } = await api.post('/admin/payment-settings', form);
        setSettings(prev => [data, ...prev]);
        toast.success('Payment setting added');
      }
      setShowForm(false);
      setEditId(null);
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      const { data } = await api.put(`/admin/payment-settings/${id}/activate`);
      setSettings(prev => prev.map(s => ({ ...s, isActive: s._id === id ? data.isActive : false })));
      toast.success('Payment setting set as active');
    } catch {
      toast.error('Failed to activate');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment setting?')) return;
    try {
      await api.delete(`/admin/payment-settings/${id}`);
      setSettings(prev => prev.filter(s => s._id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const activeEnabledMethods = (s) =>
    METHODS.filter(m => s[m.key]).map(m => m.label).join(' & ');

  return (
    <div className="aps-page">
      <div className="aps-header">
        <div>
          <div className="admin-page-title">Payment Settings</div>
          <p className="admin-page-sub">Manage mobile banking numbers shown to customers at checkout</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Number</button>
      </div>

      {/* Preview of what customers see */}
      {settings.find(s => s.isActive) && (() => {
        const active = settings.find(s => s.isActive);
        const methods = METHODS.filter(m => active[m.key]).map(m => m.label).join(' & ');
        return (
          <div className="aps-preview">
            <div className="aps-preview-label">👁 Live Customer Preview</div>
            <div className="aps-preview-box">
              <p className="aps-preview-instruction">
                Send only the <strong>shipping charge</strong> to{' '}
                <span className="aps-preview-number">{active.number}</span>
              </p>
              <p className="aps-preview-methods">{methods} available</p>
              {active.note && <p className="aps-preview-note">ℹ️ {active.note}</p>}
            </div>
          </div>
        );
      })()}

      {/* Add / Edit Form */}
      {showForm && (
        <div className="aps-form-wrap card">
          <h3>{editId ? '✏️ Edit Payment Setting' : '➕ Add Payment Number'}</h3>
          <form onSubmit={handleSubmit} className="aps-form">
            <div className="aps-form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Phone Number *</label>
                <input
                  className="form-input"
                  placeholder="e.g. 01982960886"
                  value={form.number}
                  onChange={e => setForm(f => ({ ...f, number: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Note (optional)</label>
                <input
                  className="form-input"
                  placeholder="e.g. Send exact amount"
                  value={form.note}
                  onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                />
              </div>
            </div>

            <div className="aps-methods-label">Select Available Payment Methods *</div>
            <div className="aps-methods-grid">
              {METHODS.map(m => (
                <label
                  key={m.key}
                  className={`aps-method-chip ${form[m.key] ? 'selected' : ''}`}
                  style={form[m.key] ? { borderColor: m.color, background: m.color + '18' } : {}}
                >
                  <input
                    type="checkbox"
                    checked={form[m.key]}
                    onChange={() => handleToggle(m.key)}
                    style={{ display: 'none' }}
                  />
                  <span className="aps-method-icon">{m.icon}</span>
                  <span className="aps-method-name" style={form[m.key] ? { color: m.color } : {}}>{m.label}</span>
                  {form[m.key] && <span className="aps-check" style={{ color: m.color }}>✓</span>}
                </label>
              ))}
            </div>

            <label className="aps-active-toggle">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
              />
              <span>Set as active (customers will see this number)</span>
            </label>

            <div className="aps-form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editId ? 'Update' : 'Add Number'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Settings List */}
      <div className="aps-list">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : settings.length === 0 ? (
          <div className="aps-empty">
            <div className="aps-empty-icon">💳</div>
            <p>No payment settings yet.</p>
            <button className="btn btn-primary" onClick={openAdd}>Add Your First Number</button>
          </div>
        ) : (
          settings.map(s => (
            <div key={s._id} className={`aps-card card ${s.isActive ? 'aps-card-active' : ''}`}>
              {s.isActive && <div className="aps-active-badge">✅ Active</div>}

              <div className="aps-card-top">
                <div className="aps-card-number">
                  <span className="aps-number-icon">📱</span>
                  <span className="aps-number-val">{s.number}</span>
                </div>
                <div className="aps-card-actions">
                  {!s.isActive && (
                    <button className="aps-btn aps-btn-activate" onClick={() => handleActivate(s._id)} title="Set Active">
                      ⚡ Activate
                    </button>
                  )}
                  <button className="aps-btn aps-btn-edit" onClick={() => openEdit(s)} title="Edit">✏️</button>
                  <button className="aps-btn aps-btn-delete" onClick={() => handleDelete(s._id)} title="Delete">🗑</button>
                </div>
              </div>

              <div className="aps-card-methods">
                {METHODS.map(m => (
                  <span
                    key={m.key}
                    className={`aps-method-tag ${s[m.key] ? 'on' : 'off'}`}
                    style={s[m.key] ? { borderColor: m.color, color: m.color, background: m.color + '15' } : {}}
                  >
                    {m.icon} {m.label}
                  </span>
                ))}
              </div>

              {s.note && <p className="aps-card-note">ℹ️ {s.note}</p>}

              <div className="aps-card-footer">
                <span className="aps-card-date">Added {new Date(s.createdAt).toLocaleDateString('en-BD')}</span>
                <span className="aps-customer-preview">
                  Customer sees: <em>Send shipping to {s.number} — {activeEnabledMethods(s)} available</em>
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminPaymentSettings;
